import { TextButton } from '@creditornot/cb-button';
import { Interval } from 'luxon';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { InfiniteTable } from 'modules/experience-core';
import useFtuByVenueInsights from 'modules/insights-v2/hooks/useFtuByVenueInsights';

import FtuByVenueModal from './FtuByVenueModal';
import FtuByVenueTable from './FtuByVenueTable';
import useFtuByVenueTableProps from './hooks/useFtuByVenueTableProps';
import { FtuByVenueTableSortableColumnIDs } from './types';
import { transformTableSortToApiSortBy } from './utils';
import { ContentRow, InsightEntity, RowType, VenueEntity } from '../../types';
import SummaryTable from '../InsightSummary/SummaryTable';

type FtuByVenueCardProps = {
  insightEntity: Exclude<InsightEntity, VenueEntity>;
  dateInterval: Interval;
  isVATEnabled: boolean;
};

const ROWS_LIMIT = 5;

const ShowAllButton = styled(TextButton)`
  flex-grow: 1;
  width: 100%;
  justify-content: center;
`;

const FtuByVenueCard = ({ insightEntity, dateInterval, isVATEnabled }: FtuByVenueCardProps) => {
  const { formatMessage } = useIntl();

  const [sort, setSort] = useState<InfiniteTable.SortState<FtuByVenueTableSortableColumnIDs> | undefined>(undefined);
  const ftuByVenueInsights = useFtuByVenueInsights({
    entityType: insightEntity.type,
    entityId: insightEntity.id,
    interval: dateInterval,
    sortBy: transformTableSortToApiSortBy(sort?.sortBy, isVATEnabled),
    sortOrder: sort?.sortOrder,
    limit: ROWS_LIMIT,
  });

  const tableProps = useFtuByVenueTableProps({ isVATEnabled, sort, setSort, ftuByVenueInsights, maxRows: ROWS_LIMIT });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const additionalRows: ContentRow[] = ftuByVenueInsights.hasNextPage
    ? [
        {
          type: RowType.Raw,
          key: 'show-all',
          content: (
            <ShowAllButton onClick={() => setIsModalOpen(true)}>
              <FormattedMessage id="insights.customer-base.table.show-all" />
            </ShowAllButton>
          ),
        },
      ]
    : [];

  return (
    <>
      <SummaryTable
        onRetry={ftuByVenueInsights.refetch}
        isError={ftuByVenueInsights.isError || (ftuByVenueInsights.isLoading && !ftuByVenueInsights.isFetching)}
        isFetching={ftuByVenueInsights.isFetching}
        title={formatMessage({ id: 'insights.customer-base.table.customer-base-by-venue' })}
        rows={[
          {
            type: RowType.Raw,
            key: 'raw',
            content: <FtuByVenueTable {...tableProps} />,
          },
          ...additionalRows,
        ]}
      />
      {isModalOpen && (
        <FtuByVenueModal
          insightEntity={insightEntity}
          dateInterval={dateInterval}
          isVATEnabled={isVATEnabled}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default FtuByVenueCard;
